import { z } from "zod";

// ===== SLOT STATUS =====

/**
 * Slot status enum values:
 * 0 = Unavailable
 * 1 = Available
 * 2 = Preferred
 */
export const SlotStatusSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
]);

export type SlotStatus = z.infer<typeof SlotStatusSchema>;

// ===== TIME SLOT =====

/**
 * Represents a 30-minute time slot within a day
 * Index range: 0-47 (0 = 00:00-00:30, 47 = 23:30-00:00)
 */
export const TimeSlotSchema = z.object({
  index: z.number().int().min(0).max(47),
  status: SlotStatusSchema,
});

export type TimeSlot = z.infer<typeof TimeSlotSchema>;

// ===== DAY OF WEEK =====

/**
 * Days of the week enum
 */
export const DayOfWeekSchema = z.enum([
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
  "SUN",
]);

export type DayOfWeek = z.infer<typeof DayOfWeekSchema>;

// ===== DAY SCHEDULE =====

/**
 * Schedule for a single day containing exactly 48 time slots (30-min intervals)
 */
export const DayScheduleSchema = z.object({
  dayOfWeek: DayOfWeekSchema,
  slots: z
    .array(TimeSlotSchema)
    .length(48, "Must have exactly 48 time slots (30-min intervals for 24 hours)"),
});

export type DaySchedule = z.infer<typeof DayScheduleSchema>;

// ===== WEEKLY AVAILABILITY =====

/**
 * Weekly availability schedule containing exactly 7 days (one for each day of the week)
 */
export const WeeklyAvailabilitySchema = z
  .array(DayScheduleSchema)
  .length(7, "Must have exactly 7 days")
  .refine(
    (days) => {
      // Ensure all 7 unique days are present
      const daySet = new Set(days.map((d) => d.dayOfWeek));
      return daySet.size === 7;
    },
    {
      message: "Must contain all 7 unique days of the week",
    },
  );

export type WeeklyAvailability = z.infer<typeof WeeklyAvailabilitySchema>;

// ===== HELPER CONSTANTS =====

/**
 * Slot status values for easy reference
 */
export const SLOT_STATUS = {
  UNAVAILABLE: 0,
  AVAILABLE: 1,
  PREFERRED: 2,
} as const;

/**
 * Total number of 30-minute slots in a 24-hour day
 */
export const SLOTS_PER_DAY = 48;

/**
 * Days of the week in order
 */
export const DAYS_OF_WEEK: DayOfWeek[] = [
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
  "SUN",
];


